import {
  FRAME_COLORS,
  getFrameColor,
  getFrameFromSKU,
  getHeightAndWidthToTileSizeName,
  isCenterpiece,
  MATERIAL_TYPES,
  SIZE_SEPARATOR,
  TILE_MATERIALS,
  type TILE_SIZES,
} from '@mixtiles/web-backend-shared'
import { useMaterialsAsColor, useOrderedSizes } from 'hooks/tiles'
import groupBy from 'lodash/groupBy'
import intersection from 'lodash/intersection'
import mapValues from 'lodash/mapValues'
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import useApi from '../../api/apiProvider'
import {
  getFramesApi,
  type GetFramesApiResponse,
} from '../../api/availableFramesProvider.api'
import { COUNTRIES } from '../../config/countries-data'
import useOnMount from '../../hooks/useOnMount'
import { MIXED_MATERIALS } from '../../pages/PhotoStyler/TileDesignerConsts'
import { useAddressStore } from '../../stores/RootStoreProvider'
import { countryManager } from '../../services/CountryManager'
import { useExperimentManager } from '../../services/ExperimentManager/ExperimentManager'
import { logger } from '../../services/logger'
import { PRODUCT_TYPES } from '../../services/ProductTypeState'
import { getTileStyleData } from '../../services/tilesStyling'
import { isFramedMaterial } from '../../utils/materialTypeUtils'
import { type Tile } from '../TilesProvider/TilesProvider.types'
import {
  type AvailableStockContextValue,
  type ProductToMaterialToFrameToSize,
  type ProductToMaterialToSizeToFrame,
  type StockMaterialType,
} from './AvailableStockProvider.types'
import { toJS } from 'mobx'
import { readOutsideReaction } from 'utils/mobx/readOutsideReaction'

const AvailableFramesContext = createContext<AvailableStockContextValue | null>(
  null
)
AvailableFramesContext.displayName = 'AvailableFramesContext'

// This function gets a mapping of productType to materialType to frame to sizes and transform it to product type to materialType to size to frames
function transformDataToPTToMTToSizeToFrames(
  PTToMTToFrameToSizes: ProductToMaterialToFrameToSize
): ProductToMaterialToSizeToFrame {
  return mapValues(PTToMTToFrameToSizes, (mtToFrameToSizes) =>
    mapValues(mtToFrameToSizes, (frameToSizes) => {
      const sizeToFrames = {} as Record<TILE_SIZES, FRAME_COLORS[]>
      Object.entries(frameToSizes).forEach(([frame, sizes]) => {
        sizes.forEach((size) => {
          sizeToFrames[size] = (sizeToFrames[size] ?? []).concat(
            frame as FRAME_COLORS
          )
        })
      })
      return sizeToFrames
    })
  )
}

export function transformDataToPTToMTToFrameToSizes(
  data: GetFramesApiResponse,
  isAllFramelessSizes: boolean
): ProductToMaterialToFrameToSize {
  const heightAndWidthToTileSizeName: Record<string, TILE_SIZES> =
    getHeightAndWidthToTileSizeName()
  const result = mapValues(data, (skus) =>
    mapValues(
      groupBy(
        skus.map((sku) => getFrameFromSKU(sku)),
        (frame) => frame.materialType
      ),
      (frames) =>
        mapValues(
          groupBy(frames, (frame) => frame.color),
          (frames) =>
            addFlippedSizes(frames.map((frame) => frame.size))
              .map((size) => heightAndWidthToTileSizeName[size])
              .filter((size) => size !== undefined)
        )
    )
  ) as ProductToMaterialToFrameToSize

  // Frameless shares classic stock; copy it into the FRAMELESS slot so consumers
  // can look it up directly. Only the NONE-color sizes, though — otherwise sizes
  // sold only with a frame (e.g. 12x16) would wrongly show as available frameless.
  Object.values(result).forEach((mtToFrameToSizes) => {
    const classicData = mtToFrameToSizes[MATERIAL_TYPES.CLASSIC]
    if (!classicData) return
    mtToFrameToSizes[MATERIAL_TYPES.FRAMELESS] = {
      [FRAME_COLORS.NONE]: isAllFramelessSizes
        ? [...new Set(Object.values(classicData).flat())]
        : classicData[FRAME_COLORS.NONE] ?? [],
    } as Record<FRAME_COLORS, TILE_SIZES[]>
  })

  return result
}

const FRAMES_ORDER = [
  FRAME_COLORS.BLACK,
  FRAME_COLORS.WHITE,
  FRAME_COLORS.WOOD,
  FRAME_COLORS.RED_ASH_WOOD,
  FRAME_COLORS.NONE,
]

function addFlippedSizes(sizes: string[]): string[] {
  const allSizes = new Set(sizes)
  sizes.forEach((size) => {
    const sizeComponents = size.split(SIZE_SEPARATOR)
    if (sizeComponents.length === 2) {
      const flippedSize = sizeComponents[1] + SIZE_SEPARATOR + sizeComponents[0]
      allSizes.add(flippedSize)
    }
  })
  return Array.from(allSizes)
}

export interface AvailableStockProviderProps extends PropsWithChildren {
  initialFramesData?: GetFramesApiResponse
}

const AvailableStockProvider = function AvailableStockProvider({
  children,
  initialFramesData,
}: AvailableStockProviderProps) {
  const addressStore = useAddressStore()
  const api = useApi()
  const { isEnabled: isMaterialsAsColorEnabled } = useMaterialsAsColor()
  const getOrderedSizes = useOrderedSizes()
  const experimentManager = useExperimentManager()
  const isAllFramelessSizes = experimentManager.isEnabled('frameless-all-sizes')

  const [framesData, setFramesData] = useState<GetFramesApiResponse | null>(
    initialFramesData ?? null
  )
  const [localFramesData, setLocalFramesData] =
    useState<GetFramesApiResponse | null>(null)

  // PT stands for productType
  const PTToMTToFrameToSizes = useMemo(
    () =>
      framesData
        ? transformDataToPTToMTToFrameToSizes(framesData, isAllFramelessSizes)
        : null,
    [framesData, isAllFramelessSizes]
  )
  const PTToMTToSizeToFrames = useMemo(
    () =>
      PTToMTToFrameToSizes
        ? transformDataToPTToMTToSizeToFrames(PTToMTToFrameToSizes)
        : null,
    [PTToMTToFrameToSizes]
  )
  const localSKUs = useMemo(() => {
    if (!localFramesData) return null
    const data = transformDataToPTToMTToFrameToSizes(
      localFramesData,
      isAllFramelessSizes
    )
    return transformDataToPTToMTToSizeToFrames(data)
  }, [localFramesData, isAllFramelessSizes])

  const isLoaded = PTToMTToFrameToSizes !== null

  useOnMount(() => {
    const currentAddress = readOutsideReaction(() =>
      toJS(addressStore.currentAddress)
    )
    if (currentAddress !== null) {
      initStockByAddress({
        address: currentAddress,
      })
    }
  })

  const initStockByAddress = useCallback(
    async ({ address }: { address: { countryId: string } }) => {
      try {
        const countryCode = COUNTRIES[address.countryId].code
        const response: GetFramesApiResponse = await api.call(
          getFramesApi(countryCode)
        )
        setLocalFramesData(response)
      } catch (error) {
        logger.error('Failed to get skus by zone', error)
      }
    },
    [api]
  )

  // Returns the SKUs for a country without replacing the app-wide stock, so a
  // destination the customer is still choosing can be checked in isolation.
  const getLocalSKUsByAddress = useCallback(
    async ({ address }: { address: { countryId: string } }) => {
      const countryCode = COUNTRIES[address.countryId].code
      const response: GetFramesApiResponse = await api.call(
        getFramesApi(countryCode)
      )
      return transformDataToPTToMTToSizeToFrames(
        transformDataToPTToMTToFrameToSizes(response, isAllFramelessSizes)
      )
    },
    [api, isAllFramelessSizes]
  )

  const initFrames = useCallback(async () => {
    try {
      const locale = countryManager.getCountry()
      const response: GetFramesApiResponse = await api.call(
        getFramesApi(locale)
      )
      setFramesData(response)
    } catch (error) {
      logger.error('Failed to get the available frames from the server', error)
    }
  }, [api])

  useEffect(() => {
    if (!framesData) {
      initFrames()
    }
  }, [framesData, initFrames])

  const getSupportedFramesForSize = useCallback(
    ({
      productType = PRODUCT_TYPES.CLASSIC,
      size,
      materialType = MATERIAL_TYPES.CLASSIC,
    }: {
      productType?: PRODUCT_TYPES
      size: TILE_SIZES
      materialType?: MATERIAL_TYPES
    }): FRAME_COLORS[] => {
      let supportedFrames =
        PTToMTToSizeToFrames?.[productType]?.[materialType]?.[size] ?? []
      if (
        isFramedMaterial(materialType) &&
        productType === PRODUCT_TYPES.CLASSIC
      ) {
        supportedFrames = supportedFrames.filter(
          (frame) => frame !== FRAME_COLORS.NONE
        )
      } else if (materialType === MATERIAL_TYPES.FRAMELESS) {
        if (isMaterialsAsColorEnabled) {
          supportedFrames =
            supportedFrames.length > 0 ? [FRAME_COLORS.NONE] : []
        } else {
          supportedFrames = supportedFrames.filter(
            (frame) => frame === FRAME_COLORS.NONE
          )
        }
      }

      return FRAMES_ORDER.filter((frame) => {
        return supportedFrames.includes(frame)
      })
    },
    [PTToMTToSizeToFrames, isMaterialsAsColorEnabled]
  )

  const isFrameSupportedForSize = useCallback(
    ({
      frameColor,
      size,
      materialType = MATERIAL_TYPES.CLASSIC,
    }: {
      frameColor: FRAME_COLORS
      size: TILE_SIZES
      materialType?: StockMaterialType
    }): boolean => {
      if (materialType === MIXED_MATERIALS) {
        // if the material is mixed materials than we want to check if the frame is supported at least in one material
        return TILE_MATERIALS.some((tileMaterial) =>
          isFrameSupportedForSizeInSingleMaterial({
            frameColor,
            size,
            materialType: tileMaterial,
          })
        )
      }
      return isFrameSupportedForSizeInSingleMaterial({
        frameColor,
        size,
        materialType,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getSupportedFramesForSize]
  )

  const isFrameSupportedForSizeInSingleMaterial = useCallback(
    ({
      frameColor,
      size,
      materialType = MATERIAL_TYPES.CLASSIC,
    }: {
      frameColor: FRAME_COLORS
      size: TILE_SIZES
      materialType?: MATERIAL_TYPES
    }): boolean => {
      const supportedFrames = getSupportedFramesForSize({ size, materialType })
      return supportedFrames.includes(frameColor)
    },
    [getSupportedFramesForSize]
  )

  const getSizesInStockForSingleMaterial = useCallback(
    ({ material }: { material: MATERIAL_TYPES }): TILE_SIZES[] => {
      const supportedSizes = getOrderedSizes(material)
      return supportedSizes.filter(
        (size) =>
          getSupportedFramesForSize({ size, materialType: material }).length !==
          0
      )
    },
    [getOrderedSizes, getSupportedFramesForSize]
  )

  const getSizesInStockForMaterial = useCallback(
    ({ materialType }: { materialType: StockMaterialType }): TILE_SIZES[] => {
      if (materialType === MIXED_MATERIALS) {
        const sizesInStock = TILE_MATERIALS.map((material) =>
          getSizesInStockForSingleMaterial({ material })
        )
        return [
          ...new Set(
            sizesInStock.reduce(
              (acc, currentArray) => acc.concat(currentArray),
              []
            )
          ),
        ]
      }
      return getSizesInStockForSingleMaterial({ material: materialType })
    },
    [getSizesInStockForSingleMaterial]
  )

  const isFrameSupportedForMaterial = useCallback(
    ({
      frameColor,
      material,
    }: {
      frameColor: FRAME_COLORS
      material: MATERIAL_TYPES
    }): boolean => {
      const sizes = getSizesInStockForMaterial({ materialType: material })
      return sizes.some((size) =>
        isFrameSupportedForSize({ frameColor, size, materialType: material })
      )
    },
    [getSizesInStockForMaterial, isFrameSupportedForSize]
  )

  const getSupportedFramesForSizes = useCallback(
    ({
      productType = PRODUCT_TYPES.CLASSIC,
      frameSizes,
      materialType = MATERIAL_TYPES.CLASSIC,
    }: {
      productType?: PRODUCT_TYPES
      frameSizes: TILE_SIZES[]
      materialType?: MATERIAL_TYPES
    }): FRAME_COLORS[] => {
      const supportedFramesPerSize = Object.values(frameSizes).map((size) =>
        getSupportedFramesForSize({ productType, size, materialType })
      )
      return intersection(...supportedFramesPerSize)
    },
    [getSupportedFramesForSize]
  )

  const getSupportedSizesForFrame = useCallback(
    ({
      productType = PRODUCT_TYPES.CLASSIC,
      frame,
      materialType = MATERIAL_TYPES.CLASSIC,
    }: {
      productType?: PRODUCT_TYPES
      frame: FRAME_COLORS
      materialType?: MATERIAL_TYPES
    }): TILE_SIZES[] => {
      const frameColor = getFrameColor(frame) as FRAME_COLORS
      return (
        PTToMTToFrameToSizes?.[productType]?.[materialType]?.[frameColor] ?? []
      )
    },
    [PTToMTToFrameToSizes]
  )

  const getUnsupportedTilesForSizeInSingleMaterial = useCallback(
    ({
      productType = PRODUCT_TYPES.CLASSIC,
      size,
      tiles,
      materialType = MATERIAL_TYPES.CLASSIC,
    }: {
      productType?: PRODUCT_TYPES
      size: TILE_SIZES
      tiles: Tile[]
      materialType?: MATERIAL_TYPES
    }): Tile[] => {
      const tileSizeSupportedFramesColors = getSupportedFramesForSize({
        productType,
        size,
        materialType,
      })

      if (!isFramedMaterial(materialType)) {
        return tileSizeSupportedFramesColors.length > 0 ? [] : tiles
      }

      return tiles.filter((tile) => {
        if (isCenterpiece(tile)) {
          return false
        }
        return !tileSizeSupportedFramesColors.includes(
          getTileStyleData(tile).frameColor
        )
      })
    },
    [getSupportedFramesForSize]
  )

  const getUnsupportedTilesForSize = useCallback(
    ({
      productType = PRODUCT_TYPES.CLASSIC,
      size,
      tiles,
      materialType = MATERIAL_TYPES.CLASSIC,
    }: {
      productType?: PRODUCT_TYPES
      size: TILE_SIZES
      tiles: Tile[]
      materialType?: StockMaterialType
    }): Tile[] => {
      if (materialType === MIXED_MATERIALS) {
        return tiles.filter(
          (tile) =>
            !isFrameSupportedForSizeInSingleMaterial({
              frameColor: tile.customStyle.frameColor,
              size,
              materialType: tile.materialType,
            })
        )
      }
      return getUnsupportedTilesForSizeInSingleMaterial({
        productType,
        size,
        tiles,
        materialType,
      })
    },
    [
      isFrameSupportedForSizeInSingleMaterial,
      getUnsupportedTilesForSizeInSingleMaterial,
    ]
  )

  const isSupportedSizeForMaterial = useCallback(
    ({
      size,
      material,
    }: {
      size: TILE_SIZES
      material: MATERIAL_TYPES
    }): boolean => {
      return getSizesInStockForMaterial({
        materialType: material,
      }).includes(size)
    },
    [getSizesInStockForMaterial]
  )

  const shouldDisplayFrame = useCallback(
    ({
      frameColor,
      materialType,
      forceMaterialsAsColorEnabled = false,
      skipStockCheck = false,
    }: {
      frameColor: FRAME_COLORS
      materialType: MATERIAL_TYPES
      forceMaterialsAsColorEnabled?: boolean
      skipStockCheck?: boolean
    }) => {
      // Hide 'None' frame color option if materials under frame experiment is not enabled (because they are available under the materials drawer)
      if (
        frameColor === FRAME_COLORS.NONE &&
        !isMaterialsAsColorEnabled &&
        !forceMaterialsAsColorEnabled
      ) {
        return false
      }
      // Wood frame is not available for wide frame material (it has red ash wood frame)
      if (
        frameColor === FRAME_COLORS.WOOD &&
        materialType === MATERIAL_TYPES.WIDE_FRAME
      ) {
        return false
      }
      // Red ash wood frame is not available for classic material (it has wood frame)
      if (
        frameColor === FRAME_COLORS.RED_ASH_WOOD &&
        materialType === MATERIAL_TYPES.CLASSIC
      ) {
        return false
      }

      // Canvas and frameless tiles must have a frame color of NONE. All other material types must have a frame color which is not NONE.
      if (
        [MATERIAL_TYPES.CANVAS, MATERIAL_TYPES.FRAMELESS].includes(
          materialType
        ) !==
        (frameColor == FRAME_COLORS.NONE)
      ) {
        return false
      }
      if (skipStockCheck) {
        return true
      } else {
        return isFrameSupportedForMaterial({
          frameColor,
          material: materialType,
        })
      }
    },
    [isMaterialsAsColorEnabled, isFrameSupportedForMaterial]
  )

  const getColorMaterialCombinations = useCallback(
    function* generateCombinations(
      materials: MATERIAL_TYPES[],
      forceMaterialsAsColorEnabled = false,
      skipStockCheck = false
    ) {
      for (const material of materials) {
        for (const frameColor of Object.values(FRAME_COLORS)) {
          if (
            shouldDisplayFrame({
              frameColor,
              materialType: material,
              forceMaterialsAsColorEnabled,
              skipStockCheck,
            })
          ) {
            yield { material, frameColor }
          }
        }
      }
    },
    [shouldDisplayFrame]
  )

  const contextValue: AvailableStockContextValue = {
    isLoaded,
    getSupportedFramesForSize,
    getSupportedFramesForSizes,
    getSupportedSizesForFrame,
    getUnsupportedTilesForSize,
    getSizesInStockForMaterial,
    isFrameSupportedForSize,
    isFrameSupportedForMaterial,
    isSupportedSizeForMaterial,
    initStockByAddress,
    getLocalSKUsByAddress,
    localSKUs,
    getColorMaterialCombinations,
  }

  return (
    <AvailableFramesContext.Provider value={contextValue}>
      {children}
    </AvailableFramesContext.Provider>
  )
}

function useAvailableFrames() {
  const context = useContext(AvailableFramesContext)
  if (!context) {
    throw new Error(
      'useAvailableFrames must be used within a AvailableFramesContext'
    )
  }
  return context
}

export { AvailableStockProvider, useAvailableFrames }
